import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ConfigEntityRow, ConfigHistoryRow, ReferenceGraph } from '../api';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';
import SchemaForm from '../components/SchemaForm';

interface Props { session: Session; }

export default function Editor({ session }: Props) {
  const { id = '' } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [row, setRow] = useState<ConfigEntityRow | null>(null);
  const [history, setHistory] = useState<ConfigHistoryRow[]>([]);
  const [refs, setRefs] = useState<ReferenceGraph | null>(null);
  const [schema, setSchema] = useState<Record<string, unknown> | null>(null);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [rawJson, setRawJson] = useState('');
  const [useSchemaEditor, setUseSchemaEditor] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  const load = () => {
    Promise.all([
      api.getEntity(session.gameId, id),
      api.getHistory(session.gameId, id),
      api.getReferences(session.gameId, id),
    ])
      .then(([r, h, g]) => {
        setRow(r);
        setHistory(h);
        setRefs(g);
        const dataObj = (typeof r.data === 'object' && r.data !== null ? r.data : {}) as Record<string, unknown>;
        setFormData(dataObj);
        setRawJson(JSON.stringify(r.data, null, 2));
        setDirty(false);
        // Fetch schema for this entity type
        api.getSchema(r.package, r.entity_type).then(setSchema).catch(() => setSchema(null));
      })
      .catch(e => setError(e.message));
  };
  useEffect(load, [session.gameId, id]);

  const save = async () => {
    try {
      const data = useSchemaEditor && schema ? formData : JSON.parse(rawJson);
      await api.updateEntity(session.gameId, id, { data, user_id: session.userId });
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const del = async () => {
    if (!confirm('Delete this entity? References block if still used.')) return;
    try {
      await api.deleteEntity(session.gameId, id);
      nav('/explorer');
    } catch (e) {
      setError((e as Error).message);
    }
  };

  if (!row) {
    return (
      <Container>
        <div className="empty">Loading…</div>
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={row.entity_key}
        subtitle={`${row.package} / ${row.entity_type} · v${row.version}`}
        actions={
          <>
            <span className={`badge badge-${row.status}`}>{row.status}</span>
            <button className="btn btn-primary" onClick={save} disabled={!dirty}>
              Save draft
            </button>
            <button className="btn btn-danger" onClick={del}>
              Delete
            </button>
          </>
        }
      />

      {error && <div className="error-banner">{error}</div>}

      {/* Editor card */}
      <div className="card mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="m-0 text-sm font-semibold">
            {schema && useSchemaEditor ? 'Configuration' : 'JSON payload'}
          </h3>
          <div className="flex items-center gap-3">
            {schema && (
              <label className="flex items-center gap-2 text-xs text-fg-dim cursor-pointer">
                <input
                  type="checkbox"
                  checked={useSchemaEditor}
                  onChange={e => {
                    setUseSchemaEditor(e.target.checked);
                    if (e.target.checked) {
                      // Sync raw → form
                      try { setFormData(JSON.parse(rawJson)); } catch { /* keep existing */ }
                    } else {
                      // Sync form → raw
                      setRawJson(JSON.stringify(formData, null, 2));
                    }
                  }}
                  className="accent-brand"
                />
                Form view
              </label>
            )}
            <span className="text-[11px] uppercase font-medium text-fg-dim tracking-wider">
              {dirty ? 'unsaved changes' : 'saved'}
            </span>
          </div>
        </div>

        {schema && useSchemaEditor ? (
          <div className="border border-line rounded-lg p-4 bg-bg-1">
            <SchemaForm
              schema={schema as any}
              value={formData}
              onChange={v => { setFormData(v); setDirty(true); }}
            />
          </div>
        ) : (
          <textarea
            className="input font-mono"
            value={rawJson}
            onChange={e => { setRawJson(e.target.value); setDirty(true); }}
            style={{ minHeight: 320 }}
            spellCheck={false}
          />
        )}
      </div>

      {/* References */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="card">
          <h3 className="m-0 mb-3 text-sm font-semibold">Outgoing references</h3>
          {refs?.outgoing.length ? (
            <ul className="m-0 p-0 list-none flex flex-col gap-2">
              {refs.outgoing.map((r, i) => (
                <li key={i} className="text-sm">
                  <code className="text-fg-2">
                    {r.target_package}/{r.target_entity_type}/{r.target_key}
                  </code>{' '}
                  <span className="text-fg-muted text-xs">({r.reference_type})</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-xs text-fg-muted">No outgoing references.</div>
          )}
        </div>
        <div className="card">
          <h3 className="m-0 mb-3 text-sm font-semibold">Incoming references</h3>
          {refs?.incoming.length ? (
            <ul className="m-0 p-0 list-none flex flex-col gap-2">
              {refs.incoming.map((r, i) => (
                <li key={i} className="text-sm">
                  <span className="text-fg-dim">via</span>{' '}
                  <code className="text-fg-2">{r.reference_type}</code>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-xs text-fg-muted">Nothing references this entity.</div>
          )}
        </div>
      </div>

      {/* History */}
      <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-fg-dim mt-7 mb-3">
        History
      </h2>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Version</th>
              <th>When</th>
              <th>Summary</th>
            </tr>
          </thead>
          <tbody>
            {history.map(h => (
              <tr key={h.id}>
                <td className="font-mono text-fg-dim">v{h.version}</td>
                <td className="text-xs text-fg-muted">
                  {new Date(h.changed_at).toLocaleString()}
                </td>
                <td>{h.change_summary}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Container>
  );
}
